'use strict';

import {
  classes,
  addClassToElement,
  removeClassToElement
} from '@jspartials/class-list-utils';
import {
  setHtmlUnscrollable,
  setHtmlScrollable,
  debounce,
  customEvent
} from '@jspartials/general-utils';
import { listenDOMEvent } from '@jspartials/dom-api-utils';
import Recommendations from '@jsclasses/recommendations';

const axios = require('axios');
// Classes
const SEARCHER_CLASSNAME = 'js-searcher';
const SEARCHER_BACKGROUND_CLASSNAME = 'js-searcher__background';
const SEARCHER_FORM_CLASSNAME = 'js-searcher__form';
const SEARCHER_INPUT_CLASSNAME = 'js-searcher__form-input';
const SEARCHER_INPUT_DELETE_CLASSNAME = 'js-searcher__form-input-delete';
const SEARCHER_RECOMMENDED_PHRASES_WRAPPER_CLASSNAME =
  'js-searcher_form-recommended-phrases';
const SEARCHER_RECOMMENDED_INITIAL_PHRASES_WRAPPER_CLASSNAME =
  'js-searcher_recommended-phrases-initial';
const SEARCHER_OPEN_BUTTON_CLASSNAME = 'js-searcher__button-open';
const SEARCHER_CLOSE_BUTTON_CLASSNAME = 'js-searcher__button-close';
const SEARCHER_VIEW_RESULTS_BUTTON_CLASSNAME =
  'js-searcher__view-results-button';
const SEARCHER_SUGGESTIONS_CLASSNAME = 'js-searcher__suggestions';
const SEARCHER_SUGGESTIONS_ERROR_WRAPPER_CLASSNAME =
  'js-searcher__suggestions-error';
const SEARCHER_SUGGESTIONS_ERROR_MSG_WRAPPER_CLASSNAME =
  'js-searcher__suggestions-error-msg';
const SEARCHER_WRAPPER_CLASSNAME = 'js-searcher__wrapper';
const SEARCHER_RECOMMENDATIONS_WRAPPER_CLASSNAME =
  'js-searcher__wrapper-recommendations';
const SEARCHER_SUGGESTIONS_INITIAL_WRAPPER_CLASSNAME =
  'js-searcher__wrapper-suggestions-initial';
const SEARCHER_SUGGESTIONS_INITIAL_ERROR_CLASSNAME =
  'js-searcher__suggestions-initial-error';
const SEARCHER_RECOMMENDATIONS_ERROR_CLASSNAME =
  'js-searcher__recommendations-error';
const SEARCHER_OPEN_ICON_CLASSNAME = 'js-searcher__icon-open';
const SEARCHER_CLOSE_ICON_CLASSNAME = 'js-searcher__icon-close';

// Selectors
const SEARCHER_SELECTOR = `.${SEARCHER_CLASSNAME}`;
const SEARCHER_BACKGROUND_SELECTOR = `.${SEARCHER_BACKGROUND_CLASSNAME}`;
const SEARCHER_FORM_SELECTOR = `.${SEARCHER_FORM_CLASSNAME}`;
const SEARCHER_INPUT = `.${SEARCHER_INPUT_CLASSNAME}`;
const SEARCHER_INPUT_DELETE = `.${SEARCHER_INPUT_DELETE_CLASSNAME}`;
const SEARCHER_OPEN_BUTTON_SELECTOR = `.${SEARCHER_OPEN_BUTTON_CLASSNAME}`;
const SEARCHER_CLOSE_BUTTON_SELECTOR = `.${SEARCHER_CLOSE_BUTTON_CLASSNAME}`;
const SEARCHER_VIEW_RESULTS_BUTTON_SELECTOR = `.${SEARCHER_VIEW_RESULTS_BUTTON_CLASSNAME}`;
const SEARCHER_WRAPPER_SELECTOR = `.${SEARCHER_WRAPPER_CLASSNAME}`;
const SEARCHER_RECOMMENDATIONS_ERROR_SELECTOR = `.${SEARCHER_RECOMMENDATIONS_ERROR_CLASSNAME}`;
const SEARCHER_SUGGESTIONS_INITIAL_ERROR_SELECTOR = `.${SEARCHER_SUGGESTIONS_INITIAL_ERROR_CLASSNAME}`;
const SEARCHER_SUGGESTIONS_SELECTOR = `.${SEARCHER_SUGGESTIONS_CLASSNAME}`;
const SEARCHER_SUGGESTIONS_INITIAL_WRAPPER_SELECTOR = `.${SEARCHER_SUGGESTIONS_INITIAL_WRAPPER_CLASSNAME}`;
const SEARCHER_SUGGESTIONS_ERROR_WRAPPER_SELECTOR = `.${SEARCHER_SUGGESTIONS_ERROR_WRAPPER_CLASSNAME}`;
const SEARCHER_SUGGESTIONS_ERROR_MSG_WRAPPER_SELECTOR = `.${SEARCHER_SUGGESTIONS_ERROR_MSG_WRAPPER_CLASSNAME}`;
const SEARCHER_RECOMMENDATIONS_WRAPPER_SELECTOR = `.${SEARCHER_RECOMMENDATIONS_WRAPPER_CLASSNAME}`;
const SEARCHER_RECOMMENDED_PHRASES_WRAPPER_SELECTOR = `.${SEARCHER_RECOMMENDED_PHRASES_WRAPPER_CLASSNAME}`;
const SEARCHER_RECOMMENDED_INITIAL_PHRASES_WRAPPER_SELECTOR = `.${SEARCHER_RECOMMENDED_INITIAL_PHRASES_WRAPPER_CLASSNAME}`;

const SEARCHER_OPEN_ICON_SELECTOR = `.${SEARCHER_OPEN_ICON_CLASSNAME}`;
const SEARCHER_CLOSE_ICON_SELECTOR = `.${SEARCHER_CLOSE_ICON_CLASSNAME}`;

// Elements
const searcher = document.querySelector(SEARCHER_SELECTOR);
const searcherBackground = document.querySelector(SEARCHER_BACKGROUND_SELECTOR);
const searcherForm = document.querySelector(SEARCHER_FORM_SELECTOR);
const searcherInput = document.querySelector(SEARCHER_INPUT);
const searcherInputDelete = document.querySelector(SEARCHER_INPUT_DELETE);
const searcherOpenButton = document.querySelector(
  SEARCHER_OPEN_BUTTON_SELECTOR
);
const searcherCloseButton = document.querySelector(
  SEARCHER_CLOSE_BUTTON_SELECTOR
);
const searcherWrapper = document.querySelector(SEARCHER_WRAPPER_SELECTOR);
const suggestionsWrapper = document.querySelector(
  SEARCHER_SUGGESTIONS_SELECTOR
);
const suggestionsInitialWrapper = document.querySelector(
  SEARCHER_SUGGESTIONS_INITIAL_WRAPPER_SELECTOR
);
const suggestionsInitialError = document.querySelector(
  SEARCHER_SUGGESTIONS_INITIAL_ERROR_SELECTOR
);
const suggestionsErrorWrapper = document.querySelector(
  SEARCHER_SUGGESTIONS_ERROR_WRAPPER_SELECTOR
);
const suggestionsErrorMsgWrapper = document.querySelector(
  SEARCHER_SUGGESTIONS_ERROR_MSG_WRAPPER_SELECTOR
);
const recommendationsWrapper = document.querySelector(
  SEARCHER_RECOMMENDATIONS_WRAPPER_SELECTOR
);
const recommendationsError = document.querySelector(
  SEARCHER_RECOMMENDATIONS_ERROR_SELECTOR
);
const recommendedPhrasesWrapper = document.querySelector(
  SEARCHER_RECOMMENDED_PHRASES_WRAPPER_SELECTOR
);
const searcherOpenIcon = document.querySelector(SEARCHER_OPEN_ICON_SELECTOR);
const searcherCloseIcon = document.querySelector(SEARCHER_CLOSE_ICON_SELECTOR);

// Variables
const endpointToGetInitialSuggestions = suggestionsInitialWrapper?.dataset
  ?.urlInitialSuggestions
  ? suggestionsInitialWrapper.dataset.urlInitialSuggestions
  : '';
const endpointToGetSuggestions = suggestionsWrapper?.dataset?.url
  ? suggestionsWrapper.dataset.url
  : '';
const minCharactersToGetSuggestions = 3;
const debounceSuggestions = debounce(getSuggestions, 300);
const closeMenuFromSearcher = new CustomEvent(
  customEvent.closeMenuFromSearcher
);

let controller;
let isLoading = 0; // Count of the number of calls loading
let isProcessed = false;

function openSearcher(e) {
  if (!searcher.classList.contains(classes.is.visible)) {
    if (!isProcessed) {
      getInitialSuggestions();
      initRecommendations();
      isProcessed = true;
    }
    listenDOMEvent(searcher, 'transitionstart', function () {
      if (searcher.classList.contains(classes.is.visible)) {
        searcherInput.focus();
      }
    });

    addClassToElement(searcher, classes.is.visible);
    addClassToElement(searcherOpenIcon, classes.hidden);
    removeClassToElement(searcherCloseIcon, classes.hidden);

    document.dispatchEvent(closeMenuFromSearcher); // Close Menu
    setHtmlUnscrollable();
  } else {
    closeSearcher();
  }
}

function closeSearcher() {
  if (searcher.classList.contains(classes.is.visible)) {
    setHtmlScrollable();
    clearInputTextAndSuggestionWrappers();
    searcher.scrollTo(0, 0);
    searcherInput.blur();
    removeClassToElement(searcher, classes.is.visible);
    removeClassToElement(searcherOpenIcon, classes.hidden);
    addClassToElement(searcherCloseIcon, classes.hidden);
  }
}

/**
 * Clear input value and all the suggestions related wrappers
 */
function clearInputTextAndSuggestionWrappers() {
  searcherInput.value = '';
  removeClassToElement(searcherInputDelete, classes.is.visible);
  emptySuggestionWrappers();
  if (controller) cancelAndEmptyController();
}

function emptySuggestionWrappers() {
  suggestionsWrapper.innerHTML = '';
  recommendedPhrasesWrapper.innerHTML = '';
  addClassToElement(searcherWrapper, classes.is.visible);
  addClassToElement(suggestionsErrorWrapper, classes.hidden);
  removeClassToElement(suggestionsWrapper, classes.is.visible);
  removeClassToElement(searcherBackground, classes.is.visible);
}

/**
 * Set loader
 */
function setLoader() {
  addClassToElement(searcherForm, classes.is.loading);
}

/**
 * Unset loader
 */
function unsetLoader() {
  removeClassToElement(searcherForm, classes.is.loading);
}

/**
 * Handles the input value change to show
 * certain elements and make the API call
 */
function onChangeInputValue() {
  const inputValue = searcherInput.value;
  const containsVisibleClass = searcherInputDelete.classList.contains(
    classes.is.visible
  );

  debounceSuggestions();
  if (inputValue.trim().length < minCharactersToGetSuggestions && controller)
    cancelAndEmptyController();

  if (!inputValue) {
    removeClassToElement(searcherInputDelete, classes.is.visible);
  } else {
    if (!containsVisibleClass) {
      addClassToElement(searcherInputDelete, classes.is.visible);
    }
  }
}

/**
 * Retrieve initial suggestions (Recent Searches, Trends)
 */
function getInitialSuggestions() {
  axios
    .get(endpointToGetInitialSuggestions)
    .then(function (response) {
      if (response.status === 200) {
        processResponseInitialSuggestions(response.data);
      }
    })
    .catch(function (error) {
      console.error(error);
      suggestionsInitialError.innerHTML =
        suggestionsInitialWrapper.dataset.error;
    });
}

/**
 * Checks if there is any ongoing request and cancels it
 */
function cancelRequest() {
  if (controller) {
    controller.abort();
  }
}

function cancelAndCreateController() {
  if (isLoading > 1) cancelRequest();
  controller = new AbortController();
}
function cancelAndEmptyController() {
  cancelRequest();
  controller = null;
}

/**
 * Retrieve suggestions
 */
function getSuggestions() {
  if (searcherInput.value.trim().length >= minCharactersToGetSuggestions) {
    isLoading++;
    setLoader();
    cancelAndCreateController();

    axios
      .get(
        endpointToGetSuggestions +
          encodeURIComponent(searcherInput.value.trim()),
        {
          signal: controller.signal
        }
      )
      .then(function (response) {
        if (response.status === 200) {
          processResponse(response.data);
        }
      })
      .catch(function (error) {
        console.error(error);
      })
      .then(function () {
        isLoading--;
        if (isLoading === 0) {
          unsetLoader();
        }
      });
  } else {
    emptySuggestionWrappers();
  }
}

/**
 * Process Ajax response for SearchServices-GetSuggestions
 *
 * @param {Object|String} response - Empty object literal if null response or string with rendered suggestions template contents
 */
function processResponse(response) {
  if (typeof response !== 'object') {
    removeClassToElement(searcherWrapper, classes.is.visible);
    addClassToElement(suggestionsWrapper, classes.is.visible);
    addClassToElement(searcherBackground, classes.is.visible);
    suggestionsWrapper.innerHTML = response;

    reloadVtoButtons();
    document.dispatchEvent(new Event(customEvent.listenProductTiles));

    addClassToElement(suggestionsErrorWrapper, classes.hidden);
    const recomendedPhrasesInitialWrapper = document.querySelector(
      SEARCHER_RECOMMENDED_INITIAL_PHRASES_WRAPPER_SELECTOR
    );
    const searcherViewResultsButton = document.querySelector(
      SEARCHER_VIEW_RESULTS_BUTTON_SELECTOR
    );

    if (recomendedPhrasesInitialWrapper) {
      recommendedPhrasesWrapper.innerHTML =
        recomendedPhrasesInitialWrapper.innerHTML;
    } else {
      recommendedPhrasesWrapper.innerHTML = '';
    }

    listenDOMEvent(searcherViewResultsButton, 'click', function () {
      searcherForm.submit();
    });
  } else {
    emptySuggestionWrappers();
    if (response && response.queryString) {
      let queryString = decodeURI(response.queryString.split('=')[1]);
      suggestionsErrorMsgWrapper.innerHTML = queryString;
      removeClassToElement(suggestionsErrorWrapper, classes.hidden);
    }
  }
}

/**
 * Process Ajax response for SearchServices-GetInitialSuggestions
 *
 * @param {Object|String} response - Empty object literal if null response or string with rendered suggestions template contents
 */
function processResponseInitialSuggestions(response) {
  if (typeof response !== 'object') {
    if (response.trim() === '') {
      suggestionsInitialError.innerHTML =
        suggestionsInitialWrapper.dataset.error;
    } else {
      suggestionsInitialWrapper.innerHTML = response;
    }
  } else {
    emptySuggestionWrappers();
  }
}

/**
 * Uses einstein to generate the master products
 */
function initRecommendations() {
  new Recommendations({
    wrapper: recommendationsWrapper,
    showPrice: false,
    maxVisibleItems: 3
  });
}

const reloadVtoButtons = () => {
  const eventReloadVtoButtons = new CustomEvent('reloadVtoButtons');
  document.dispatchEvent(eventReloadVtoButtons);
};

document.addEventListener('DOMContentLoaded', function () {
  document.addEventListener('closeSearcherFromMenu', () => {
    closeSearcher();
  });

  if (searcherOpenButton) {
    listenDOMEvent(searcherOpenButton, 'click', openSearcher);
  }

  if (searcherCloseButton) {
    listenDOMEvent(searcherCloseButton, 'click', closeSearcher);
  }

  if (searcherInputDelete) {
    listenDOMEvent(
      searcherInputDelete,
      'click',
      clearInputTextAndSuggestionWrappers
    );
  }

  if (searcherInput) {
    listenDOMEvent(searcherInput, 'input', onChangeInputValue);
  }

  /**
   * Close modal when click outside
   */
  listenDOMEvent(window, 'click', function (event) {
    if (
      !event.target.closest(SEARCHER_BACKGROUND_SELECTOR) &&
      !event.target.closest(SEARCHER_SUGGESTIONS_SELECTOR) &&
      !event.target.closest('.js-header__content-options') &&
      searcher &&
      searcher.classList.contains(classes.is.visible)
    ) {
      closeSearcher();
    }
  });
});
